import React, { useRef, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, FileButton, Group, Select, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle, IconDownload, IconFileText, IconLock, IconTrash, IconUpload,
} from '@tabler/icons-react';

import {
  uploadSource, deleteSource, classifySource, updateSourceRole, answerOpenQuestion, applyDesignEdits,
} from '../api_scripts/solnStudioClient';
import { extractText, ACCEPT_ATTR } from '../utils/extractText';
import { ROLE_OPTIONS, DEFAULT_ROLE } from '../utils/docRoles';
import { extractWorksheetAnswers } from '../utils/customerWorksheet';
import ActionHelp from '../components/ActionHelp';

const KIND_COLORS = { pdf: 'red', docx: 'blue', xlsx: 'green', text: 'gray' };

// Static downloadable workshop template (served from public/). Named per-platform so other Solution
// Studio platforms can drop sibling templates into the same folder later.
const WORKSHOP_TEMPLATE_URL =
  `${process.env.PUBLIC_URL || ''}/solnstudio_files/workshop-templates/Amazon_Connect_Workshop_Template.docx`;

export default function SourcesPanel({ project, designModel, getApiToken, onChanged, locked }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // transient "extracting/uploading X…" line
  const [error, setError] = useState(null);
  const resetRef = useRef(null);

  const docs = project.sourceDocs || [];

  // Upload files sequentially: each upload bumps the project version, so we re-read it (via onChanged)
  // before the next one to keep the optimistic-concurrency `expectedVersion` fresh.
  const onPick = async (files) => {
    if (!files || (Array.isArray(files) && files.length === 0)) return;
    const list = Array.isArray(files) ? files : [files];
    setBusy(true);
    setError(null);
    let expectedVersion = project.version;
    let existing = docs;
    let answersApplied = 0;
    let questionsAdded = 0;
    const openQuestions = designModel?.openQuestions || [];
    try {
      for (const file of list) {
        setStatus(`Extracting ${file.name}…`);
        const { text, kind, contentType } = await extractText(file);

        // If this is a re-uploaded customer worksheet, deterministically (a) resolve filled-in answers
        // to existing questions (matched by `#`/num) and (b) create any NEW questions the engineer typed
        // in spare rows — before registering the doc, so the next refine already sees them.
        if (kind === 'xlsx') {
          try {
            const { answers, added } = await extractWorksheetAnswers(file, openQuestions);
            for (const a of answers) {
              setStatus(`Applying answer to #${a.num}…`);
              const { project: updated } = await answerOpenQuestion(
                project.projectId,
                a.oqId,
                { expectedVersion, answer: a.answer, status: 'resolved' },
                getApiToken,
              );
              expectedVersion = updated.version;
              answersApplied += 1;
            }
            if (added.length) {
              setStatus(`Adding ${added.length} new question(s)…`);
              const ops = added.map((a) => ({
                op: 'add',
                path: '/openQuestions',
                value: { question: a.question, category: a.category, ...(a.answer ? { status: 'resolved', answer: a.answer } : {}) },
              }));
              const res = await applyDesignEdits(project.projectId, expectedVersion, ops, getApiToken);
              expectedVersion = res.project.version;
              questionsAdded += added.length;
            }
          } catch (e) {
            console.warn('worksheet round-trip failed; continuing with plain upload', e);
          }
        }

        // Auto-suggest a semantic role from the text (best-effort; never block the upload on it).
        setStatus(`Classifying ${file.name}…`);
        let role = DEFAULT_ROLE;
        try {
          const suggestion = await classifySource(
            project.projectId,
            { filename: file.name, kind, excerpt: text.slice(0, 8000) },
            getApiToken,
          );
          if (suggestion?.role) role = suggestion.role;
        } catch (e) {
          console.warn('classifySource failed; defaulting role', e);
        }
        setStatus(`Uploading ${file.name}…`);
        const { project: updated } = await uploadSource(
          project.projectId,
          expectedVersion,
          { filename: file.name, contentType, kind, text, role },
          getApiToken,
        );
        expectedVersion = updated.version;
        existing = updated.sourceDocs || existing;
      }
      if (answersApplied > 0 || questionsAdded > 0) {
        const bits = [];
        if (answersApplied > 0) bits.push(`resolved ${answersApplied}`);
        if (questionsAdded > 0) bits.push(`added ${questionsAdded} new`);
        setStatus(`Customer worksheet — open questions: ${bits.join(' · ')} (see the Design tab).`);
        setTimeout(() => setStatus(null), 6000);
      } else {
        setStatus(null);
      }
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running on this project — uploads are paused until it finishes.'
        : err.reason === 'stale-version'
        ? 'This project changed while uploading. Reopen the project and retry.'
        : err.message);
      setStatus(null);
      onChanged(); // resync whatever did land
    } finally {
      setBusy(false);
      resetRef.current?.(); // clear FileButton so the same file can be re-picked
    }
  };

  const remove = async (doc) => {
    setBusy(true);
    setError(null);
    try {
      await deleteSource(project.projectId, project.version, doc.id, getApiToken);
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running — deletes are paused until it finishes.'
        : err.message);
    } finally {
      setBusy(false);
    }
  };

  // Engineer confirms / overrides the auto-suggested role for a doc.
  const setRole = async (doc, role) => {
    if (!role || role === doc.role) return;
    setBusy(true);
    setError(null);
    try {
      await updateSourceRole(project.projectId, project.version, doc.id, role, getApiToken);
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running — role changes are paused until it finishes.'
        : err.reason === 'stale-version'
        ? 'This project changed — reopen it and retry.'
        : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="xs">
          <div>
            <Title order={4}>Source documents</Title>
            <Text c="dimmed" size="sm">
              Running a design workshop? Download the Amazon Connect Workshop Template, capture answers,
              then upload it here. Text is extracted in your browser and seeds the Design Model when you
              run a generation pass. PDFs / Word / Excel / CSV / notes are all accepted. Each upload is
              auto-tagged by purpose (SOW, existing environment, integration docs, design input) — adjust
              the role if the suggestion is off.
            </Text>
          </div>
          <Group gap="xs">
            <Tooltip
              label="Download the blank Amazon Connect workshop questionnaire. Fill it in with the customer, then upload it back here as a Source."
              withArrow multiline w={300}
            >
              <Button
                component="a"
                href={WORKSHOP_TEMPLATE_URL}
                download
                variant="light"
                color="orange"
                leftSection={<IconDownload size={16} />}
              >
                Workshop template
              </Button>
            </Tooltip>
            <FileButton onChange={onPick} accept={ACCEPT_ATTR} multiple resetRef={resetRef} disabled={busy || locked}>
              {(props) => (
                <Tooltip
                  label={<ActionHelp
                    reads="the files you pick (text extracted in your browser — PDF / Word / Excel / CSV / notes)"
                    does="auto-tags each by role; a filled Customer Worksheet auto-resolves matching open questions"
                    updates="adds them to Sources, ready for the next Design pass"
                  />}
                  withArrow multiline w={300}
                >
                  <Button {...props} color="orange" leftSection={<IconUpload size={16} />} loading={busy}>
                    Upload documents
                  </Button>
                </Tooltip>
              )}
            </FileButton>
          </Group>
        </Group>

        {locked && (
          <Alert color="blue" icon={<IconLock size={16} />} mb="sm">
            A generation job is running — uploads and deletes are paused until it finishes.
          </Alert>
        )}
        {status && <Text size="sm" c="dimmed" mb="sm">{status}</Text>}
        {error && <Alert color="red" icon={<IconAlertCircle size={16} />} mb="sm">{error}</Alert>}

        {docs.length === 0 ? (
          <Text c="dimmed" size="sm">No source documents yet.</Text>
        ) : (
          <Stack gap="xs">
            {docs.map((d) => (
              <Group key={d.id} justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <IconFileText size={16} />
                  <Text size="sm" lineClamp={1}>{d.filename}</Text>
                  <Badge size="xs" variant="light" color={KIND_COLORS[d.kind] || 'gray'}>{d.kind}</Badge>
                  <Text size="xs" c="dimmed">{(d.sizeChars || 0).toLocaleString()} chars</Text>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <Tooltip label="What this document is — guides how the design generator weights it" withArrow>
                    <Select
                      size="xs"
                      w={230}
                      data={ROLE_OPTIONS}
                      value={d.role || DEFAULT_ROLE}
                      onChange={(v) => setRole(d, v)}
                      disabled={busy || locked}
                      allowDeselect={false}
                      aria-label={`Role for ${d.filename}`}
                    />
                  </Tooltip>
                  <ActionIcon variant="subtle" color="red" onClick={() => remove(d)} disabled={busy || locked} title="Remove">
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            ))}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
